import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, AlertTriangle } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const POLL_MS = 5000;

/**
 * Global polling modal: while a client is authenticated we poll
 * /api/me/redemptions/pending every 5s. If the manager initiated a
 * bonus redemption, we surface a full-screen confirmation modal.
 *
 * Layered above any page (z-index 2000). Rendered in MobileLayout.
 */
export default function PendingRedemptionModal() {
  const { user } = useAuth();
  const [pending, setPending] = useState(null);
  const [acting, setActing] = useState(null); // 'confirm' | 'decline' | null
  const [error, setError] = useState("");
  const dismissedRef = useRef(new Set()); // ids declined locally to avoid re-popups

  useEffect(() => {
    if (!user || user.role === "admin") return;

    let alive = true;
    const tick = async () => {
      try {
        const r = await axios.get(`${API}/me/redemptions/pending`, { withCredentials: true });
        if (!alive) return;
        const list = Array.isArray(r.data) ? r.data : [];
        const fresh = list.find(d => !dismissedRef.current.has(d.id));
        setPending(prev => {
          // Avoid resetting if same request still pending
          if (prev && fresh && prev.id === fresh.id) return prev;
          return fresh || null;
        });
      } catch { /* network blip — keep polling */ }
    };

    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, [user]);

  const confirm = async () => {
    if (!pending || acting) return;
    setActing("confirm"); setError("");
    try {
      await axios.post(`${API}/redemptions/${pending.id}/confirm`, {}, { withCredentials: true });
      setPending(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось подтвердить");
    } finally {
      setActing(null);
    }
  };

  const decline = async () => {
    if (!pending || acting) return;
    setActing("decline"); setError("");
    try {
      await axios.post(`${API}/redemptions/${pending.id}/decline`, {}, { withCredentials: true });
      dismissedRef.current.add(pending.id);
      setPending(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось отклонить");
    } finally {
      setActing(null);
    }
  };

  return (
    <AnimatePresence>
      {pending && (
        <motion.div
          data-testid="pending-redemption-modal"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
            backdropFilter: "blur(10px)", zIndex: 2000,
            display: "flex", alignItems: "center", justifyContent: "center", padding: "20px",
          }}
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.25 }}
            style={{
              background: "linear-gradient(135deg,#1A1408 0%,#2E2010 100%)",
              border: "1px solid var(--ang-gold)",
              borderRadius: "16px",
              padding: "28px 24px",
              width: "100%",
              maxWidth: "400px",
              boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
            }}
          >
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: "56px", height: "56px", borderRadius: "50%",
              background: "var(--ang-gold-soft)", border: "1px solid var(--ang-gold)",
              margin: "0 auto 16px",
            }}>
              <AlertTriangle size={28} style={{ color: "var(--ang-gold)" }} />
            </div>

            <div style={{
              fontFamily: "Cormorant Garamond", fontSize: "24px", color: "var(--ang-text)",
              textAlign: "center", marginBottom: "6px", fontWeight: 500,
            }}>
              Списание бонусов
            </div>
            <div style={{
              fontFamily: "Manrope", fontSize: "12px", color: "var(--ang-text-muted)",
              textAlign: "center", marginBottom: "20px", letterSpacing: "0.05em",
            }}>
              Менеджер просит подтвердить операцию
            </div>

            <div style={{
              background: "rgba(0,0,0,0.4)", borderRadius: "10px", padding: "16px",
              marginBottom: "20px", border: "1px solid var(--ang-gold-soft)",
            }}>
              <Row label="Покупка" value={`${Math.floor(pending.purchase_amount).toLocaleString("ru-RU")} ₸`} />
              <Row label="К списанию с бонусов" value={`-${Math.floor(pending.bonus_amount).toLocaleString("ru-RU")} ₸`} accent />
              <div style={{ height: "1px", background: "var(--ang-gold-soft)", margin: "10px 0" }} />
              <Row
                label="К оплате"
                value={`${Math.floor(pending.purchase_amount - pending.bonus_amount).toLocaleString("ru-RU")} ₸`}
                bold
              />
              {pending.description && (
                <div style={{
                  marginTop: "12px", paddingTop: "10px",
                  borderTop: "1px solid var(--ang-border)",
                  fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-text-muted)",
                }}>
                  {pending.description}
                </div>
              )}
            </div>

            <div style={{
              fontFamily: "Manrope", fontSize: "11px", color: "var(--ang-gold-strong)",
              textAlign: "center", marginBottom: "16px", lineHeight: 1.5,
            }}>
              Кэшбэк начислится от полной суммы покупки.<br />
              Если это не вы — отклоните.
            </div>

            {error && (
              <div data-testid="redemption-modal-error" style={{
                background: "rgba(220,38,38,0.15)", border: "1px solid #DC2626",
                color: "#FCA5A5", padding: "10px", borderRadius: "6px",
                fontFamily: "Manrope", fontSize: "12px", marginBottom: "12px", textAlign: "center",
              }}>{error}</div>
            )}

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                data-testid="decline-redemption-btn"
                onClick={decline}
                disabled={!!acting}
                style={{
                  flex: 1,
                  background: "transparent", border: "1px solid var(--ang-border-strong)",
                  color: "var(--ang-text-soft)",
                  borderRadius: "8px", padding: "13px",
                  fontFamily: "Manrope", fontSize: "12px", fontWeight: 700,
                  letterSpacing: "0.08em", textTransform: "uppercase",
                  cursor: acting ? "not-allowed" : "pointer",
                  opacity: acting === "decline" ? 0.6 : 1,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                }}
              >
                <X size={14} /> {acting === "decline" ? "Отклоняем…" : "Отклонить"}
              </button>
              <button
                data-testid="confirm-redemption-btn"
                onClick={confirm}
                disabled={!!acting}
                style={{
                  flex: 1.4,
                  background: "linear-gradient(135deg,var(--ang-gold),var(--ang-gold-2))",
                  border: "none", color: "var(--ang-bg)",
                  borderRadius: "8px", padding: "13px",
                  fontFamily: "Manrope", fontSize: "12px", fontWeight: 700,
                  letterSpacing: "0.08em", textTransform: "uppercase",
                  cursor: acting ? "not-allowed" : "pointer",
                  opacity: acting === "confirm" ? 0.7 : 1,
                  display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
                }}
              >
                <Check size={14} /> {acting === "confirm" ? "Подтверждаем…" : "Подтвердить"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Row({ label, value, accent, bold }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "baseline",
      padding: "5px 0",
      fontFamily: "Manrope",
      fontSize: bold ? "16px" : "13px",
      color: accent ? "#FCA5A5" : "var(--ang-text-soft)",
      fontWeight: bold ? 700 : 500,
    }}>
      <span style={{ color: "var(--ang-text-muted)", fontSize: "12px", fontWeight: 500 }}>{label}</span>
      <span style={{ color: bold ? "var(--ang-gold)" : (accent ? "#FCA5A5" : "white") }}>{value}</span>
    </div>
  );
}
